import { useParams, Link } from "react-router-dom";
import SEO from "../components/seo/SEO";
import Breadcrumbs from "../components/seo/Breadcrumbs";
import { getCalculator } from "../config/calculators";
import { getCategory } from "../config/categories";
import CalculatorEngine from "../components/calculator/CalculatorEngine";
import EducationalContent from "../components/calculator/EducationalContent";
import CalculatorCard from "../components/calculator/CalculatorCard";
import Disclaimer from "../components/common/Disclaimer";
import AdPlaceholder from "../components/common/AdPlaceholder";
import { webApplicationSchema, faqSchema, breadcrumbSchema, mergeSchema } from "../lib/seo";
import { DATA_SOURCES } from "../lib/financial-data/canada";
import NotFound from "./NotFound";

export default function CalculatorPage() {
  const { slug } = useParams();
  const calc = getCalculator(slug);
  if (!calc) return <NotFound />;
  const cat = getCategory(calc.category);

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Calculators", to: "/calculators" },
    { label: cat.name, to: `/calculators/category/${cat.id}` },
    { label: calc.name, to: `/calculators/${calc.slug}` },
  ];

  const schema = mergeSchema(
    webApplicationSchema(calc),
    faqSchema(calc.faq),
    breadcrumbSchema(crumbs),
  );

  const related = (calc.relatedTools || []).map(getCalculator).filter(Boolean);

  return (
    <>
      <SEO title={calc.seo.title} description={calc.seo.description} path={`/calculators/${calc.slug}`} keywords={calc.seo.keywords} schema={schema} />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={crumbs} />

        <header className="mb-8 max-w-3xl">
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground sm:text-5xl">{calc.name}</h1>
          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{calc.tagline}</p>
          <p className="mt-3 text-xs text-muted-foreground">Applicable year: <strong>{calc.applicableYear}</strong> · Last updated: {calc.lastUpdated}</p>
        </header>

        <CalculatorEngine calc={calc} />

        <div className="my-10"><AdPlaceholder slot="calc-under-tool" /></div>

        <div className="mt-4 grid gap-10 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <EducationalContent calc={calc} />
          </div>
          <aside className="space-y-6">
            <div className="sticky top-20 space-y-6">
              <Disclaimer variant="box" year={calc.applicableYear} source={cat.name} lastUpdated={calc.lastUpdated} />
              <div className="rounded-2xl border border-border bg-card p-5">
                <h3 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide text-foreground">Canadian data sources</h3>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  {DATA_SOURCES.map((d, i) => (
                    <li key={i}><strong className="text-foreground/80">{d.label}:</strong> {d.source} ({d.year})</li>
                  ))}
                </ul>
                <p className="mt-3 text-xs italic text-muted-foreground">Verify current-year figures before launch.</p>
              </div>
              <AdPlaceholder slot="calc-sidebar" />
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-16">
            <h2 className="mb-6 font-heading text-2xl font-bold tracking-tight text-foreground">Related calculators</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((c) => <CalculatorCard key={c.slug} calc={c} />)}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
